import { type FC } from "react";
import {
  Alert,
  AppBar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogContent,
  Divider,
  IconButton,
  LinearProgress,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Toolbar,
  Tooltip,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ClearIcon from "@mui/icons-material/Clear";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import { useIsMobile } from "@/shared/hooks";
import type { UploadItem } from "./useMultipleUploads";

interface UploadsDialogProps {
  items: UploadItem[];
  onClose: () => void;
  onCancelItem: (key: string) => void;
  /** Клон референсного onCreateContents (UploadDialog.js) — создаёт записи
   * контента по успешно загруженным файлам. Вызывается по клику "Далее". */
  onConfirm: () => void;
  creating?: boolean;
}

// [MAP] по трём финальным статусам загрузки — "uploading" рендерится
// отдельной веткой (проценты, а не текстовый лейбл), поэтому в мапу не входит.
const UPLOAD_STATUS_LABEL_KEYS: Record<
  Exclude<UploadItem["status"], "uploading">,
  string
> = {
  done: "videos.uploads.statusDone",
  error: "videos.uploads.statusError",
  canceled: "videos.uploads.statusCanceled",
};

const statusChipProps = (
  item: UploadItem,
  t: TFunction
): { color: "primary" | "success" | "error" | "default"; label: string } => {
  switch (item.status) {
    case "done":
      return { color: "success", label: t(UPLOAD_STATUS_LABEL_KEYS.done) };
    case "error":
      return { color: "error", label: t(UPLOAD_STATUS_LABEL_KEYS.error) };
    case "canceled":
      return {
        color: "default",
        label: t(UPLOAD_STATUS_LABEL_KEYS.canceled),
      };
    default:
      return { color: "primary", label: `${item.progress}%` };
  }
};

export const UploadsDialog: FC<UploadsDialogProps> = ({
  items,
  onClose,
  onCancelItem,
  onConfirm,
  creating = false,
}) => {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const allDone = items.every((i) => i.status !== "uploading");

  return (
    <Dialog
      open={items.length > 0}
      onClose={onClose}
      fullScreen={isMobile}
      maxWidth="sm"
      fullWidth
    >
      {/* Эталонная компоновка диалогов кабинета (см. docs/CRITICAL-RULES.md):
          AppBar-шапка elevation={0} на background.default + Divider —
          отделяет её от тела на дефолтном Paper диалога. Кнопка закрытия —
          слева (edge="start"), как в референсном DialogHeader.js, и доступна
          в любой момент: onClose (clear()) аварийно прерывает ещё идущие
          загрузки, поэтому закрывать диалог не дожидаясь конца — безопасно. */}
      <AppBar color="default" position="sticky" elevation={0}>
        <Toolbar>
          <IconButton
            edge="start"
            onClick={onClose}
            size="large"
            sx={{ mr: 1 }}
          >
            <CloseIcon />
          </IconButton>
          <Typography variant="h6" noWrap sx={{ flex: 1, mr: 1 }}>
            {t("videos.uploads.dialogTitle")}
          </Typography>
          <Tooltip title={allDone ? "" : t("videos.uploads.waitTooltip")}>
            <span>
              <Button
                variant="contained"
                disableElevation
                disabled={!allDone || creating}
                onClick={onConfirm}
              >
                {t("videos.uploads.nextButton")}
              </Button>
            </span>
          </Tooltip>
        </Toolbar>
        <Divider />
      </AppBar>
      <DialogContent sx={{ p: 1 }}>
        <Alert
          icon={false}
          severity={allDone ? "success" : "warning"}
          variant="filled"
          sx={{ mb: 1 }}
        >
          {allDone
            ? t("videos.uploads.alertComplete")
            : t("videos.uploads.alertUploading")}
        </Alert>

        <Card elevation={0}>
          <CardContent>
            <List sx={{ py: 0 }}>
              {items.map((item) => {
                const chip = statusChipProps(item, t);

                return (
                  <ListItem key={item.key} sx={{ px: 0 }}>
                    <ListItemAvatar>
                      <Box
                        sx={{
                          position: "relative",
                          width: isMobile ? 110 : 150,
                          mr: 2,
                        }}
                      >
                        <Box
                          sx={{
                            position: "absolute",
                            left: 2,
                            top: 2,
                            zIndex: 2,
                          }}
                        >
                          <Chip
                            size="small"
                            color={chip.color}
                            label={chip.label}
                          />
                        </Box>
                        <Box
                          sx={{
                            width: "100%",
                            position: "relative",
                            height: 0,
                            paddingTop: "56.25%", // 16:9
                            borderRadius: 1,
                            overflow: "hidden",
                            bgcolor: "action.hover",
                          }}
                        >
                          <Box
                            component="video"
                            autoPlay
                            muted
                            loop
                            src={item.fileUrl}
                            sx={{
                              objectFit: "cover",
                              position: "absolute",
                              inset: 0,
                              width: "100%",
                              height: "100%",
                            }}
                          />
                          <LinearProgress
                            variant="determinate"
                            color={
                              item.status === "error"
                                ? "error"
                                : item.status === "done"
                                  ? "success"
                                  : "primary"
                            }
                            value={
                              item.status === "error" ? 100 : item.progress
                            }
                            sx={{
                              position: "absolute",
                              left: 0,
                              right: 0,
                              bottom: 0,
                            }}
                          />
                        </Box>
                      </Box>
                    </ListItemAvatar>

                    <ListItemText
                      sx={{ overflow: "hidden", textOverflow: "ellipsis" }}
                      primary={item.fileName}
                      secondary={
                        item.status === "done"
                          ? t(UPLOAD_STATUS_LABEL_KEYS.done)
                          : item.status === "error"
                            ? t("videos.uploads.statusErrorDescription")
                            : item.status === "canceled"
                              ? t(UPLOAD_STATUS_LABEL_KEYS.canceled)
                              : undefined
                      }
                    />

                    <IconButton
                      aria-label={t("videos.uploads.cancelItemAria")}
                      onClick={() => onCancelItem(item.key)}
                    >
                      <ClearIcon />
                    </IconButton>
                  </ListItem>
                );
              })}
            </List>
          </CardContent>
        </Card>
      </DialogContent>
    </Dialog>
  );
};
